import React from 'react';

import { useParams } from 'react-router-dom';
import { PageParams } from '../types/PageParams';
import useCustomerEmail from '../hooks/use-customer-email';
import NotFoundPage from 'src/components/NotFoundPage/NotFoundPage';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { OpportunityEmailSender } from 'src/api/v2';
import { useCluster } from 'src/features/cluster';
import '../styles/CustomerCommunication.scss';
import { Button } from '@innovationnorway/component-library';
import { useAuth } from 'src/features/authorization';
import NoAccess from 'src/components/NoAccess/NoAccess';
import { useTranslation } from 'react-i18next';
import CreateEmailThreadModal from '../components/customer-communication/CreateEmailThreadModal';
import useOneWayIn from '../../hooks/use-one-way-in';

type EmailLogPageParams = PageParams & {
  threadId: string;
};

const EmailLog: React.FC = () => {
  const { cluster, isLoading: clusterLoading } = useCluster();
  const { threadId, formId } = useParams<EmailLogPageParams>();
  const { details, isLoading } = useCustomerEmail(parseInt(threadId ?? '0', 0), formId);
  const { oneWayInOpportunityById, oneWayInGroupedFormWOEnrollmentsQuery } = useOneWayIn(formId);
  const { checkAccess } = useAuth();
  const { read, write } = checkAccess('OneWayInCustomerCommunication');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  if (isLoading || clusterLoading) {
    return <LoadingPage />;
  }
  if (!details) {
    return <NotFoundPage />;
  }
  if (!read) {
    return <NoAccess />;
  }

  return (
    <div className="stack">
      <h2>{details.title}</h2>
      <div className="display--flex flex-direction--column gap--2">
        {details.emails.map((email) => {
          const isClusterEmail = email.sender == OpportunityEmailSender.Cluster;
          return (
            <div
              key={email.bodyText.substring(0, 64).split(' ').join('-')}
              className={`email-log ${isClusterEmail ? 'cluster' : 'customer'}`}
            >
              <p className="email-log__names">
                <strong>{tCommon('from')}:</strong> {isClusterEmail ? cluster?.name : details.recipientEmail}
              </p>

              <p className="email-log__content" dangerouslySetInnerHTML={{ __html: email.bodyText }}></p>
            </div>
          );
        })}
      </div>

      <Button type="button" onClick={() => setIsModalOpen(true)} disabled={!write}>
        {tOneWayIn('communication.sendNewEmail')}
      </Button>
      {isModalOpen && write && (
        <CreateEmailThreadModal
          formId={formId}
          thread={details}
          show={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          opportunity={oneWayInOpportunityById.data}
          groupedForm={oneWayInGroupedFormWOEnrollmentsQuery.data}
        />
      )}
    </div>
  );
};

export default EmailLog;
